import jsPDF from 'jspdf';

// Premium branding colors matching FURD branded reports
const NAVY = '#1e3a5f';
const GREY = '#64748b';
const LIGHT_GREY = '#f8fafc';
const BLUE_ACCENT = '#3b82f6';
const DARK_GREY = '#334155';
const GOLD_ACCENT = '#d4a853';

// Layout constants — consistent across all PDFs
const M = 20; // Standard margin
const FOOTER_HEIGHT = 28;
const HEADER_HEIGHT = 52;
const DEFAULT_LOGO_PATH = '/furd-tech-logo.png';

let defaultLogoBase64 = null;

const loadImageAsBase64 = async (url) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    console.error('Failed to load image:', err);
    return null;
  }
};

const getDefaultLogo = async () => {
  if (defaultLogoBase64) return defaultLogoBase64;
  defaultLogoBase64 = await loadImageAsBase64(DEFAULT_LOGO_PATH);
  return defaultLogoBase64;
};

// --- Helpers ---

/** Safe Y position — returns the max Y before the footer */
const safeBottom = (doc) => doc.internal.pageSize.getHeight() - FOOTER_HEIGHT - 10;

/** Check if we need a new page. If so, add page and return reset Y. */
const ensureSpace = (doc, yPos, needed) => {
  if (yPos + needed > safeBottom(doc)) {
    doc.addPage();
    return M + 10;
  }
  return yPos;
};

/** Wrap text to fit width, print it, return new Y. Handles page breaks mid-text. */
const printWrapped = (doc, text, x, y, maxWidth, lineHeight = 4.5) => {
  if (!text) return y;
  const lines = doc.splitTextToSize(String(text), maxWidth);
  for (let i = 0; i < lines.length; i++) {
    y = ensureSpace(doc, y, lineHeight + 2);
    doc.text(lines[i], x, y);
    y += lineHeight;
  }
  return y;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '-'; }
};

const formatCurrency = (val) => {
  const num = Number(val) || 0;
  return `£${num.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// --- Header & Footer ---

const addPremiumHeader = (doc, branding, documentTitle, documentSubtitle = null, logoBase64 = null) => {
  const pageWidth = doc.internal.pageSize.getWidth();

  // Navy header bar
  doc.setFillColor(NAVY);
  doc.rect(0, 0, pageWidth, HEADER_HEIGHT, 'F');

  // Gold accent line
  doc.setFillColor(GOLD_ACCENT);
  doc.rect(0, HEADER_HEIGHT - 2, pageWidth, 2, 'F');

  // Logo
  let logoEndX = M;
  const logoData = branding?.logo_data || logoBase64;
  if (logoData && logoData.startsWith('data:image')) {
    try {
      doc.addImage(logoData, 'PNG', M, 10, 32, 32);
      logoEndX = M + 36;
    } catch { logoEndX = M; }
  }

  // Tagline area (left of company name) — removed per branding request
  const tagX = logoEndX + (logoEndX === M ? 0 : 4);

  // Center: company name
  const companyName = branding?.company_name || 'FURD LTD';
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor('#ffffff');
  doc.text(companyName, pageWidth / 2, 25, { align: 'center' });

  // Right: document title + subtitle + date
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor('#ffffff');
  doc.text(documentTitle.toUpperCase(), pageWidth - M, 18, { align: 'right' });
  if (documentSubtitle) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor('#cbd5e1');
    doc.text(documentSubtitle, pageWidth - M, 28, { align: 'right' });
  }
  doc.setFontSize(8);
  doc.setTextColor('#94a3b8');
  doc.text(formatDate(new Date().toISOString()), pageWidth - M, 38, { align: 'right' });

  return HEADER_HEIGHT + 10;
};

const addPremiumFooter = (doc, branding, pageNum, totalPages) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const footerY = pageHeight - FOOTER_HEIGHT + 10;
  const companyName = branding?.company_name || 'FURD LTD';

  doc.setFillColor(GOLD_ACCENT);
  doc.rect(0, footerY - 10, pageWidth, 1, 'F');
  doc.setFillColor('#fefce8');
  doc.rect(0, footerY - 9, pageWidth, FOOTER_HEIGHT + 9, 'F');

  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(NAVY);
  doc.text(companyName, M, footerY);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(GREY);
  doc.text(`Generated: ${formatDate(new Date().toISOString())}`, M, footerY + 6);

  doc.setTextColor(NAVY);
  doc.setFont('helvetica', 'bold');
  doc.text(`${pageNum}`, pageWidth / 2, footerY, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(GREY);
  doc.text(`of ${totalPages}`, pageWidth / 2 + 8, footerY);

  doc.setFontSize(7);
  doc.setTextColor('#94a3b8');
  doc.text('CONFIDENTIAL', pageWidth - M, footerY, { align: 'right' });
};

// --- Section helpers ---

const addSectionHeader = (doc, title, y, options = {}) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const color = options.color || NAVY;
  y = ensureSpace(doc, y, 18);

  doc.setFillColor(color);
  doc.rect(M, y, pageWidth - M * 2, 9, 'F');
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor('#ffffff');
  doc.text(title.toUpperCase(), M + 5, y + 6.5);
  return y + 15;
};

const addKeyValue = (doc, key, value, x, y, options = {}) => {
  const maxW = options.maxWidth || 70;
  y = ensureSpace(doc, y, 7);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(GREY);
  doc.text(key, x, y);

  doc.setTextColor(DARK_GREY);
  doc.setFont('helvetica', 'bold');
  const valStr = String(value || '-');
  // Wrap long values
  const lines = doc.splitTextToSize(valStr, maxW);
  doc.text(lines[0], x + 48, y);
  if (lines.length > 1) {
    for (let i = 1; i < lines.length; i++) {
      y += 4.5;
      doc.text(lines[i], x + 48, y);
    }
  }
  return y + 6;
};

const addInfoCard = (doc, x, y, w, h, title, lines) => {
  doc.setFillColor(LIGHT_GREY);
  doc.roundedRect(x, y, w, h, 3, 3, 'F');
  doc.setDrawColor('#e2e8f0');
  doc.setLineWidth(0.5);
  doc.roundedRect(x, y, w, h, 3, 3, 'S');

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(GREY);
  doc.text(title, x + 8, y + 8);

  let lineY = y + 16;
  doc.setTextColor(DARK_GREY);
  lines.forEach((line, idx) => {
    if (!line) return;
    if (idx === 0) {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
    } else {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
    }
    // Truncate to fit card width
    const maxChars = Math.floor((w - 16) / 2);
    const text = String(line).substring(0, maxChars);
    doc.text(text, x + 8, lineY);
    lineY += idx === 0 ? 7 : 5;
  });
};

const addFootersToAllPages = (doc, branding) => {
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    addPremiumFooter(doc, branding, i, totalPages);
  }
};

// ============================================================
// QUOTE PDF
// ============================================================

export const generateQuotePDF = async (quote, enquiry, decision, branding = null) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const contentW = pageWidth - M * 2;
  const defaultLogo = !branding?.logo_data ? await getDefaultLogo() : null;

  let y = addPremiumHeader(doc, branding, 'QUOTATION', quote?.quote_number || 'DRAFT', defaultLogo);

  // Status badge
  const status = (quote?.status || 'draft').toUpperCase();
  const statusColors = { WON: '#10b981', APPROVED: '#3b82f6', SENT: '#8b5cf6', PENDING_APPROVAL: '#f59e0b', DRAFT: '#64748b', LOST: '#ef4444' };
  doc.setFillColor(statusColors[status] || GREY);
  doc.roundedRect(pageWidth - M - 35, HEADER_HEIGHT + 4, 35, 8, 2, 2, 'F');
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor('#ffffff');
  doc.text(status.replace('_', ' '), pageWidth - M - 17.5, HEADER_HEIGHT + 9.5, { align: 'center' });

  // Meta row
  y += 5;
  const metaItems = [
    { label: 'DATE ISSUED', value: formatDate(quote?.created_at) },
    { label: 'VALID UNTIL', value: quote?.created_at ? formatDate(new Date(new Date(quote.created_at).getTime() + (quote?.validity_days || 30) * 86400000).toISOString()) : '-' },
    { label: 'REFERENCE', value: enquiry?.reference_number || '-' },
    { label: 'VALIDITY', value: `${quote?.validity_days || 30} days` },
  ];
  const metaW = contentW / metaItems.length;
  metaItems.forEach((item, i) => {
    const x = M + i * metaW;
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(GREY);
    doc.text(item.label, x, y);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(DARK_GREY);
    doc.text(item.value, x, y + 6);
  });
  y += 18;

  // Client & Project cards
  const cardW = (contentW - 10) / 2;
  const cardH = 48;
  addInfoCard(doc, M, y, cardW, cardH, 'CLIENT', [
    enquiry?.client_name,
    enquiry?.client_company || enquiry?.client_name,
    enquiry?.client_email,
    enquiry?.client_phone
  ]);
  addInfoCard(doc, M + cardW + 10, y, cardW, cardH, 'PROJECT', [
    enquiry?.project_name,
    enquiry?.location,
    enquiry?.project_type?.replace(/_/g, ' ').toUpperCase() || '-',
    enquiry?.sector?.replace(/_/g, ' ') || '-'
  ]);
  y += cardH + 12;

  // Scope / Disciplines
  const disciplines = quote?.disciplines || enquiry?.disciplines;
  if (disciplines && disciplines.length > 0) {
    y = addSectionHeader(doc, 'Scope of Work', y);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(DARK_GREY);
    const scopeText = disciplines.map(d => d.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())).join('  |  ');
    y = printWrapped(doc, scopeText, M, y, contentW);
    y += 8;
  }

  // Line items
  y = ensureSpace(doc, y, 30);
  y = addSectionHeader(doc, 'Line Items', y);

  // Table header
  doc.setFillColor('#f1f5f9');
  doc.rect(M, y, contentW, 10, 'F');
  y += 7;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(GREY);
  doc.text('DESCRIPTION', M + 4, y);
  doc.text('DISC.', M + 80, y);
  doc.text('QTY', M + 110, y);
  doc.text('RATE', M + 125, y);
  doc.text('TOTAL', pageWidth - M - 4, y, { align: 'right' });
  y += 8;

  const items = quote?.line_items || [];
  items.forEach((item, idx) => {
    y = ensureSpace(doc, y, 12);
    if (idx % 2 === 0) {
      doc.setFillColor('#fafafa');
      doc.rect(M, y - 4, contentW, 9, 'F');
    }
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(DARK_GREY);

    // Wrap description if long
    const descLines = doc.splitTextToSize(item.description || '-', 72);
    doc.text(descLines[0], M + 4, y);
    doc.setTextColor(GREY);
    doc.text((item.discipline || '-').substring(0, 12), M + 80, y);
    doc.text(String(item.quantity || 1), M + 110, y);
    doc.text(formatCurrency(item.unit_price || 0), M + 125, y);
    doc.setTextColor(DARK_GREY);
    doc.setFont('helvetica', 'bold');
    doc.text(formatCurrency(item.total || 0), pageWidth - M - 4, y, { align: 'right' });
    doc.setFont('helvetica', 'normal');

    y += 9;
    // Print wrapped lines for long descriptions
    if (descLines.length > 1) {
      doc.setFontSize(7);
      doc.setTextColor(GREY);
      for (let i = 1; i < descLines.length; i++) {
        y = ensureSpace(doc, y, 5);
        doc.text(descLines[i], M + 4, y);
        y += 4;
      }
    }
  });

  y += 10;

  // Totals box
  y = ensureSpace(doc, y, 50);
  const totalsX = pageWidth - M - 85;
  doc.setFillColor(LIGHT_GREY);
  doc.roundedRect(totalsX - 10, y - 5, 95, 48, 3, 3, 'F');

  y += 5;
  doc.setFontSize(9);
  doc.setTextColor(GREY);
  doc.text('Subtotal', totalsX, y);
  doc.setTextColor(DARK_GREY);
  doc.text(formatCurrency(quote?.subtotal || 0), pageWidth - M - 4, y, { align: 'right' });

  y += 8;
  doc.setTextColor(GREY);
  doc.text(`VAT (${quote?.vat_rate || 20}%)`, totalsX, y);
  doc.setTextColor(DARK_GREY);
  doc.text(formatCurrency(quote?.vat_amount || 0), pageWidth - M - 4, y, { align: 'right' });

  y += 5;
  doc.setDrawColor('#cbd5e1');
  doc.setLineWidth(0.5);
  doc.line(totalsX, y, pageWidth - M, y);

  y += 10;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(NAVY);
  doc.text('TOTAL', totalsX, y);
  doc.setFontSize(14);
  doc.text(formatCurrency(quote?.total || 0), pageWidth - M - 4, y, { align: 'right' });
  y += 20;

  // Payment Terms
  if (quote?.payment_terms || quote?.terms_conditions) {
    y = ensureSpace(doc, y, 25);
    y = addSectionHeader(doc, 'Terms & Conditions', y, { color: '#64748b' });
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(DARK_GREY);
    const termsText = quote?.payment_terms || quote?.terms_conditions || '';
    y = printWrapped(doc, termsText, M, y, contentW, 4);
    y += 5;
  }

  // Notes
  if (quote?.notes) {
    y = ensureSpace(doc, y, 20);
    y = addSectionHeader(doc, 'Notes', y, { color: '#475569' });
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(DARK_GREY);
    y = printWrapped(doc, quote.notes, M, y, contentW);
  }

  addFootersToAllPages(doc, branding);
  return doc;
};

// ============================================================
// ENQUIRY SUMMARY PDF
// ============================================================

export const generateEnquirySummaryPDF = async (data, branding = null) => {
  const { enquiry, decision, quotes, activity, creator_name, days_open, total_quote_value, won_value } = data;
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const contentW = pageWidth - M * 2;
  const col2 = pageWidth / 2 + 5;
  const defaultLogo = !branding?.logo_data ? await getDefaultLogo() : null;

  let y = addPremiumHeader(doc, branding, 'ENQUIRY SUMMARY', enquiry?.reference_number || '-', defaultLogo);

  // Status badge
  const statusColors = { won: '#10b981', lost: '#ef4444', quoted: '#3b82f6', new: '#64748b', under_review: '#f59e0b', decision_made: '#8b5cf6' };
  doc.setFillColor(statusColors[enquiry?.status] || GREY);
  doc.roundedRect(pageWidth - M - 35, HEADER_HEIGHT + 4, 35, 8, 2, 2, 'F');
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor('#ffffff');
  doc.text((enquiry?.status || 'new').replace(/_/g, ' ').toUpperCase(), pageWidth - M - 17.5, HEADER_HEIGHT + 9.5, { align: 'center' });

  // --- Project Overview ---
  y = addSectionHeader(doc, 'Project Overview', y);

  const sectorDisplay = enquiry?.sector === 'other' && enquiry?.sector_custom_name
    ? `Other (${enquiry.sector_custom_name})`
    : enquiry?.sector?.replace(/_/g, ' ');

  // Left column
  y = addKeyValue(doc, 'Project:', enquiry?.project_name, M, y);
  y = addKeyValue(doc, 'Client:', enquiry?.client_name, M, y);
  y = addKeyValue(doc, 'Company:', enquiry?.client_company || enquiry?.client_name, M, y);
  y = addKeyValue(doc, 'Location:', enquiry?.location, M, y);
  y = addKeyValue(doc, 'Type:', enquiry?.project_type?.replace(/_/g, ' ').toUpperCase(), M, y);
  y = addKeyValue(doc, 'Sector:', sectorDisplay, M, y);

  // Right column (start from same baseline as left)
  let y2 = y - 36;
  y2 = addKeyValue(doc, 'Est. Value:', formatCurrency(enquiry?.estimated_value || 0), col2, y2);
  y2 = addKeyValue(doc, 'Area:', `${(enquiry?.sqft || 0).toLocaleString('en-GB')} sqft`, col2, y2);
  y2 = addKeyValue(doc, 'Days Open:', `${days_open || 0} days`, col2, y2);
  y2 = addKeyValue(doc, 'Source:', enquiry?.source?.replace(/_/g, ' ') || '-', col2, y2);
  y2 = addKeyValue(doc, 'Created By:', creator_name || '-', col2, y2);
  y2 = addKeyValue(doc, 'Priority:', (enquiry?.urgency || enquiry?.priority || 'Standard').replace(/_/g, ' '), col2, y2);
  y = Math.max(y, y2) + 5;

  // --- Client Contact ---
  y = addSectionHeader(doc, 'Client Contact', y);
  y = addKeyValue(doc, 'Name:', enquiry?.client_name, M, y);
  y = addKeyValue(doc, 'Email:', enquiry?.client_email, M, y);
  y = addKeyValue(doc, 'Phone:', enquiry?.client_phone, M, y);
  if (enquiry?.client_position) {
    y = addKeyValue(doc, 'Position:', enquiry?.client_position, M, y);
  }
  y += 3;

  // --- MEP Disciplines ---
  if (enquiry?.disciplines && enquiry.disciplines.length > 0) {
    y = addSectionHeader(doc, 'MEP Disciplines', y);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(DARK_GREY);
    enquiry.disciplines.forEach((d) => {
      y = ensureSpace(doc, y, 6);
      doc.text(`•  ${d.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}`, M + 4, y);
      y += 5.5;
    });
    y += 5;
  }

  // --- Timeline ---
  y = addSectionHeader(doc, 'Project Timeline', y);
  y = addKeyValue(doc, 'Target Start:', formatDate(enquiry?.timeline_start), M, y);
  addKeyValue(doc, 'Completion:', formatDate(enquiry?.timeline_end), col2, y - 6);
  y += 5;

  // --- Decision Engine ---
  if (decision) {
    y = ensureSpace(doc, y, 35);
    y = addSectionHeader(doc, 'Decision Engine Result', y);

    const outcomeText = decision.outcome || decision.decision || 'Pending';
    const decisionColor = outcomeText.toLowerCase().includes('quote') ? '#10b981' :
                          outcomeText.toLowerCase().includes('review') ? '#f59e0b' : '#ef4444';

    doc.setFillColor(decisionColor);
    doc.roundedRect(M, y, 40, 9, 2, 2, 'F');
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor('#ffffff');
    doc.text(outcomeText.toUpperCase().substring(0, 15), M + 20, y + 6.5, { align: 'center' });

    let scoreDisplay = '';
    const scoreStr = String(decision.score || '');
    if (scoreStr.includes('/')) {
      const match = scoreStr.match(/(\d+)\/(\d+)/);
      if (match) {
        const passed = parseInt(match[1], 10);
        const total = parseInt(match[2], 10);
        scoreDisplay = `${passed} of ${total} steps passed (${total > 0 ? Math.round((passed / total) * 100) : 0}%)`;
      }
    } else if (typeof decision.score === 'number') {
      scoreDisplay = `Score: ${decision.score}%`;
    }

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(NAVY);
    doc.text(scoreDisplay, M + 48, y + 6);
    y += 18;

    if (decision.reasoning) {
      doc.setFontSize(9);
      doc.setTextColor(GREY);
      doc.text('Reasoning:', M, y);
      y += 5;
      doc.setTextColor(DARK_GREY);
      doc.setFont('helvetica', 'normal');
      y = printWrapped(doc, decision.reasoning, M, y, contentW);
      y += 5;
    }
  }

  // --- Quotes ---
  if (quotes && quotes.length > 0) {
    y = ensureSpace(doc, y, 40);
    y = addSectionHeader(doc, `Quotes (${quotes.length})`, y);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(NAVY);
    doc.text(`Total: ${formatCurrency(total_quote_value || 0)}`, M, y);
    if (won_value > 0) {
      doc.setTextColor('#10b981');
      doc.text(`Won: ${formatCurrency(won_value)}`, M + 70, y);
    }
    y += 10;

    // Table header
    doc.setFillColor('#f1f5f9');
    doc.rect(M, y, contentW, 9, 'F');
    y += 6;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(GREY);
    doc.text('QUOTE #', M + 4, y);
    doc.text('STATUS', M + 45, y);
    doc.text('TOTAL', M + 80, y);
    doc.text('DATE', M + 120, y);
    y += 7;

    doc.setFont('helvetica', 'normal');
    quotes.slice(0, 8).forEach((q) => {
      y = ensureSpace(doc, y, 7);
      doc.setTextColor(DARK_GREY);
      doc.text(q.quote_number || '-', M + 4, y);
      const qStatus = q.status || 'draft';
      doc.setTextColor(qStatus === 'won' ? '#10b981' : qStatus === 'lost' ? '#ef4444' : GREY);
      doc.setFont('helvetica', 'bold');
      doc.text(qStatus.toUpperCase(), M + 45, y);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(DARK_GREY);
      doc.text(formatCurrency(q.total || 0), M + 80, y);
      doc.text(formatDate(q.created_at), M + 120, y);
      y += 6;
    });
    y += 5;
  }

  // --- Activity ---
  if (activity && activity.length > 0) {
    y = ensureSpace(doc, y, 30);
    y = addSectionHeader(doc, 'Recent Activity', y, { color: '#475569' });
    doc.setFontSize(8);
    activity.slice(0, 12).forEach((act) => {
      y = ensureSpace(doc, y, 6);
      doc.setTextColor(GREY);
      doc.text(formatDate(act.timestamp), M, y);
      doc.setTextColor(DARK_GREY);
      const action = (act.action || '-').replace(/_/g, ' ').substring(0, 35);
      doc.text(action, M + 28, y);
      doc.setTextColor(GREY);
      doc.text(`by ${act.user_name || 'Unknown'}`, M + 95, y);
      y += 5;
    });
    y += 5;
  }

  // --- Description ---
  if (enquiry?.description) {
    y = ensureSpace(doc, y, 20);
    y = addSectionHeader(doc, 'Project Description', y, { color: '#64748b' });
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(DARK_GREY);
    y = printWrapped(doc, enquiry.description, M, y, contentW);
  }

  addFootersToAllPages(doc, branding);
  return doc;
};

// ============================================================
// WEEKLY REPORT PDF
// ============================================================

export const generateReportPDF = async (reportData, branding = null) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const contentW = pageWidth - M * 2;
  const defaultLogo = !branding?.logo_data ? await getDefaultLogo() : null;

  let y = addPremiumHeader(doc, branding, 'PERFORMANCE REPORT', reportData?.week_label || 'This Week', defaultLogo);

  // KPI Cards
  y += 5;
  const kpis = [
    { label: 'Enquiries', value: reportData?.enquiries_count || 0, color: NAVY },
    { label: 'Quotes', value: reportData?.quotes_count || 0, color: BLUE_ACCENT },
    { label: 'Won', value: reportData?.won_count || 0, color: '#10b981' },
    { label: 'Outreach', value: reportData?.outreach_count || 0, color: '#8b5cf6' }
  ];

  const cardWidth = (contentW - 15) / 4;
  kpis.forEach((kpi, idx) => {
    const x = M + idx * (cardWidth + 5);
    doc.setFillColor(LIGHT_GREY);
    doc.roundedRect(x, y, cardWidth, 30, 4, 4, 'F');
    doc.setFillColor(kpi.color);
    doc.roundedRect(x, y, cardWidth, 4, 4, 4, 'F');
    doc.setFontSize(8);
    doc.setTextColor(GREY);
    doc.text(kpi.label.toUpperCase(), x + cardWidth / 2, y + 12, { align: 'center' });
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(kpi.color);
    doc.text(String(kpi.value), x + cardWidth / 2, y + 24, { align: 'center' });
  });
  y += 45;

  // Value summary + conversion rates
  doc.setFillColor(LIGHT_GREY);
  doc.roundedRect(M, y, contentW, 40, 4, 4, 'F');

  const valCol = contentW / 3;
  // Pipeline
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(GREY);
  doc.text('PIPELINE VALUE', M + 12, y + 10);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(NAVY);
  doc.text(formatCurrency(reportData?.pipeline_value || 0), M + 12, y + 25);

  // Won
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(GREY);
  doc.text('WON VALUE', M + valCol + 8, y + 10);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor('#10b981');
  doc.text(formatCurrency(reportData?.won_value || 0), M + valCol + 8, y + 25);

  // Win rate
  const totalQuotes = (reportData?.quotes_count || 0);
  const wonCount = (reportData?.won_count || 0);
  const winRate = totalQuotes > 0 ? Math.round((wonCount / totalQuotes) * 100) : 0;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(GREY);
  doc.text('WIN RATE', M + valCol * 2 + 8, y + 10);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(winRate >= 30 ? '#10b981' : winRate > 0 ? '#f59e0b' : GREY);
  doc.text(`${winRate}%`, M + valCol * 2 + 8, y + 25);

  // Conversion subtitle
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(GREY);
  doc.text(`${wonCount} won of ${totalQuotes} quoted`, M + valCol * 2 + 8, y + 32);

  y += 55;

  // Activity by Day
  y = addSectionHeader(doc, 'Activity by Day', y);

  if (reportData?.activity_by_day) {
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const maxActivity = Math.max(...Object.values(reportData.activity_by_day), 1);
    const barW = (contentW - 30) / 7;
    const maxBarH = 40;

    days.forEach((day, idx) => {
      const value = reportData.activity_by_day[day.toLowerCase()] || 0;
      const barH = (value / maxActivity) * maxBarH;
      const x = M + 15 + idx * barW;

      doc.setFillColor('#e2e8f0');
      doc.roundedRect(x, y, barW - 6, maxBarH, 2, 2, 'F');
      if (barH > 0) {
        doc.setFillColor(BLUE_ACCENT);
        doc.roundedRect(x, y + maxBarH - barH, barW - 6, barH, 2, 2, 'F');
      }
      doc.setFontSize(8);
      doc.setTextColor(GREY);
      doc.text(day, x + (barW - 6) / 2, y + maxBarH + 8, { align: 'center' });
      if (value > 0) {
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(NAVY);
        doc.text(String(value), x + (barW - 6) / 2, y + maxBarH - barH - 3, { align: 'center' });
      }
    });
  }
  y += 60;

  // Team Performance
  if (reportData?.user_performance && reportData.user_performance.length > 0) {
    y = ensureSpace(doc, y, 30);
    y = addSectionHeader(doc, 'Team Performance', y);

    doc.setFillColor('#f1f5f9');
    doc.rect(M, y, contentW, 9, 'F');
    y += 6;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(GREY);
    doc.text('TEAM MEMBER', M + 4, y);
    doc.text('ENQUIRIES', M + 65, y);
    doc.text('QUOTES', M + 95, y);
    doc.text('OUTREACH', M + 120, y);
    doc.text('SCORE', pageWidth - M - 4, y, { align: 'right' });
    y += 8;

    doc.setFont('helvetica', 'normal');
    reportData.user_performance.slice(0, 10).forEach((user, idx) => {
      y = ensureSpace(doc, y, 9);
      if (idx % 2 === 0) {
        doc.setFillColor('#fafafa');
        doc.rect(M, y - 4, contentW, 8, 'F');
      }
      doc.setTextColor(DARK_GREY);
      doc.text((user.name || '-').substring(0, 25), M + 4, y);
      doc.setTextColor(GREY);
      doc.text(String(user.enquiries || 0), M + 65, y);
      doc.text(String(user.quotes || 0), M + 95, y);
      doc.text(String(user.outreach || 0), M + 120, y);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(NAVY);
      doc.text(String(user.score || 0), pageWidth - M - 4, y, { align: 'right' });
      doc.setFont('helvetica', 'normal');
      y += 8;
    });
  }

  addFootersToAllPages(doc, branding);
  return doc;
};

// ============================================================
// Utilities
// ============================================================

export const downloadPDF = (doc, filename) => {
  doc.save(filename);
};

/** Legacy wrapper for backward compatibility */
export const generateSummaryPDF = async (summaryData, enquiry) => {
  return generateEnquirySummaryPDF({
    enquiry,
    decision: summaryData?.decision,
    quotes: summaryData?.quotes || [],
    activity: summaryData?.activity || [],
    creator_name: summaryData?.creator_name || 'Unknown',
    days_open: summaryData?.days_open || 0,
    total_quote_value: summaryData?.total_quote_value || 0,
    won_value: summaryData?.won_value || 0
  }, summaryData?.branding);
};
